import { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SerializedError } from '@reduxjs/toolkit';
import { analytics, events } from 'services/analytics';
import distributionApi from 'services/api/distribution';
import {
  useReleaseNavigation,
  useCreateRelease,
} from 'services/hooks/distribution';
import ConversionDialog from '../ConversionDialog';
import { useAudioFileContext } from './context';
import InstructionsContainer from './components/InstructionsContainer';
import { Dropzone } from './components';
import MessagesContainer from './components/MessagesContainer';
import AudioFileInfoContainer from './components/AudioFileInfoContainer';

interface PropsType {
  isOpen: boolean;
  onClose: () => void;
  onCancelUpload: () => void;
}

const UploadAudioModal = ({ isOpen, onClose, onCancelUpload }: PropsType) => {
  const { t } = useTranslation();

  const navigateToReleaseFlow = useReleaseNavigation();

  const { audioFile, validationError, setAudioFile, setUploadRequest } =
    useAudioFileContext();

  const {
    createNewRelease,
    data: release,
    isLoading: isReleaseLoading,
  } = useCreateRelease({ hasUserAudio: true });

  const [
    uploadAudio,
    {
      data: uploadedAudio,
      isLoading: isUploadAudioLoading,
      isSuccess: isUploadAudioSuccess,
      error: uploadAudioError,
      reset: resetUploadAudio,
    },
  ] = distributionApi.useUploadAudioMutation();

  useEffect(() => {
    if (!release) {
      return;
    }

    navigateToReleaseFlow(release.id);
  }, [release, navigateToReleaseFlow]);

  const audioFileError = useMemo(
    () => (uploadAudioError || validationError) as { message: string },
    [uploadAudioError, validationError]
  );

  const isDisabled = useMemo(
    () =>
      !audioFile ||
      isUploadAudioLoading ||
      isReleaseLoading ||
      !!audioFileError,
    [audioFile, isUploadAudioLoading, isReleaseLoading, audioFileError]
  );

  const submitButtonText = useMemo(() => {
    if (isUploadAudioLoading) {
      return t('distribution:uploadAudioModal:uploadButton:uploading');
    }
    if (isUploadAudioSuccess) {
      return t('distribution:uploadAudioModal:uploadButton:continue');
    }
    return t('distribution:uploadAudioModal:uploadButton:upload');
  }, [t, isUploadAudioLoading, isUploadAudioSuccess]);

  useEffect(() => {
    if (!audioFile) {
      return;
    }
    analytics.track(events.releaseUploadFileAdded());
  }, [audioFile]);

  useEffect(() => {
    if (!isUploadAudioSuccess) {
      return;
    }
    analytics.track(events.releaseUploadSuccess());
  }, [isUploadAudioSuccess]);

  useEffect(() => {
    if (!uploadAudioError) {
      return;
    }
    analytics.track(
      events.releaseUploadError(
        (uploadAudioError as SerializedError)?.message ?? ''
      )
    );
  }, [uploadAudioError]);

  const handleDeleteAudioFile = useCallback(() => {
    setAudioFile(null);
    resetUploadAudio();
  }, [resetUploadAudio, setAudioFile]);

  const handleUpload = useCallback(() => {
    if (uploadedAudio) {
      createNewRelease({ songId: uploadedAudio.id });
      return;
    }
    if (!audioFile) {
      return;
    }
    const request = uploadAudio(audioFile);
    setUploadRequest(request);
  }, [
    uploadedAudio,
    audioFile,
    uploadAudio,
    createNewRelease,
    setUploadRequest,
  ]);

  const handleCancelUpload = useCallback(() => {
    if (!audioFile || !!audioFileError) {
      onClose();
      return;
    }

    onCancelUpload();
  }, [audioFile, audioFileError, onCancelUpload, onClose]);

  return (
    <ConversionDialog
      title={t('distribution:uploadAudioModal:title')}
      state={{
        isOpen,
        error: null,
        isLoading: false,
        isSubmitDisabled: isDisabled,
      }}
      action={{
        onSubmit: handleUpload,
        onClose: handleCancelUpload,
      }}
      submitText={submitButtonText}
      closeText={t('distribution:uploadAudioModal:cancel')}
    >
      {audioFile ? (
        <AudioFileInfoContainer
          isLoading={isUploadAudioLoading}
          isError={!!audioFileError}
          isSuccess={isUploadAudioSuccess}
          onDeleteButtonClick={handleDeleteAudioFile}
        />
      ) : (
        <Dropzone />
      )}
      <MessagesContainer
        isSuccess={isUploadAudioSuccess}
        isLoading={isUploadAudioLoading}
        error={audioFileError}
      />
      <InstructionsContainer />
    </ConversionDialog>
  );
};

export default UploadAudioModal;
